import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, HeartIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, XIcon, TrendingUpIcon, PlusIcon, PackageIcon, ClipboardListIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon, TrashIcon, ChevronDownIcon, ClockIcon, MenuIcon, ScanBarcodeIcon, TruckIcon, ShieldCheckIcon, StethoscopeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useStore } from '../../lib/store';
import { trackViewCart } from '../../lib/analytics/events';
import { haptics } from '../../lib/utils/haptics';
import { AnnouncementBar } from './AnnouncementBar';
import { AuthModal } from '../modals/AuthModal';
import { LocationModal } from '../modals/LocationModal';
import { OrderTrackingModal } from '../modals/OrderTrackingModal';
import { TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
interface DesktopHeaderProps {
  locationModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  selectedLocation: {
    name: string;
  } | null;
  setSelectedLocation: (location: {
    name: string;
  } | null) => void;
  disableShrink?: boolean;
  showAnnouncement?: boolean;
}
export function DesktopHeader({
  locationModalOpen,
  setLocationModalOpen,
  selectedLocation,
  setSelectedLocation,
  disableShrink = false,
  showAnnouncement = false
}: DesktopHeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    language,
    setLanguage,
    cartCount,
    cartTotal,
    searchQuery,
    setSearchQuery,
    addToCart,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWishlistMenu, setShowWishlistMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [orderTrackingModalOpen, setOrderTrackingModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const wishlistCount = wishlistItems.length;
  const cartTotalValue = cartTotal();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node) && searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };
  const handleMouseEnter = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(true);
    if (menu === 'wishlist') setShowWishlistMenu(true);
    if (menu === 'cart') {
      setShowCartMenu(true);
      if (cartItems.length > 0) {
        trackViewCart(cartItems.map(item => ({
          item_id: item.id.toString(),
          item_name: item.name,
          price: item.price,
          quantity: item.quantity
        })));
      }
    }
  };
  const handleMouseLeave = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(false);
    if (menu === 'wishlist') setShowWishlistMenu(false);
    if (menu === 'cart') setShowCartMenu(false);
  };
  const goals = [{
    name: 'Muscle Building',
    icon: '💪',
    slug: 'muscle-building'
  }, {
    name: 'Weight Loss',
    icon: '⚖️',
    slug: 'weight-loss'
  }, {
    name: 'Energy & Endurance',
    icon: '⚡',
    slug: 'energy-endurance'
  }, {
    name: 'Recovery',
    icon: '🔄',
    slug: 'recovery'
  }, {
    name: 'General Health',
    icon: '❤️',
    slug: 'general-health'
  }, {
    name: 'Mass Gain',
    icon: '📈',
    slug: 'mass-gain'
  }];
  const getSearchContent = () => {
    if (searchQuery.length === 0) return null;
    return <div className="w-full" data-id="element-2712" data-mp="16c9cmkllk36">
        <h3 className="text-xs font-bold text-[#62617A] uppercase tracking-wider mb-4" data-id="element-2713" data-mp="3tbpoxk57spf2">
          Matching Products
        </h3>
        <div className="space-y-4" data-id="element-2714" data-mp="230l1a4wvkw8w">
          {TRENDING_PRODUCTS.slice(0, 4).map(product => <div key={product.id} className="flex items-center gap-3 p-2 hover:bg-[#F3F2FA] rounded-xl transition-colors group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-id="element-2715" data-mp="1yzjc6gwoa0kn">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-[#DEDCF0]" data-id="element-2716" data-mp="1f4horaljk4ls">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-2717" data-mp="1k31d1hnlxshb" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2718" data-mp="1a6y9q8h0rnss">
                <h4 className="text-sm font-medium text-[#333273] line-clamp-2 leading-tight mb-1 group-hover:text-[#333273] transition-colors" data-id="element-2719" data-mp="w5vrk6t5axn5">
                  {product.name}
                </h4>
                <div className="flex items-center gap-2" data-id="element-2720" data-mp="2qjwp8ocyleln">
                  <span className="text-sm font-bold text-[#333273]" data-id="element-2721" data-mp="2qkeugahln35p">
                    {product.price.toFixed(2)} SAR
                  </span>
                  {product.discount > 0 && <span className="text-[10px] text-[#62617A] line-through" data-id="element-2722" data-mp="2t5e4dgxclf3c">
                      {product.originalPrice?.toFixed(2)}
                    </span>}
                </div>
                {product.discount > 0 && <div className="mt-1 inline-block bg-red-100 text-red-700 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-tighter" data-id="element-2723" data-mp="2f4tksijlzf8s">
                    {product.discount}% OFF
                  </div>}
              </div>
              <button onClick={e => {
            e.stopPropagation();
            haptics.success();
            addToCart({
              id: product.id,
              name: product.name,
              price: product.price,
              image: product.image
            });
            toast.success(`${product.name} added to cart!`);
          }} className="w-8 h-8 flex items-center justify-center bg-white border border-[#DEDCF0] text-[#333273] rounded-full hover:bg-[#333273] hover:text-white transition-all shadow-sm" data-id="element-2724" data-mp="37tr0qf0ckzyd">
                <PlusIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-2725" data-mp="bw1dkyo87hs2" />
              </button>
            </div>)}
        </div>
      </div>;
  };
  return <div className="hidden md:block fixed top-0 left-0 right-0 z-[100] transition-all duration-300" data-id="element-2726" data-mp="gw5x6xkfksmi">
      {/* Main Header */}
      <div className="bg-white border-b border-[#E3E1EF]" data-id="element-2727" data-mp="pvj7fsgdpl8a">
        <div className="container mx-auto px-4" data-id="element-2728" data-mp="ptydo51w26bh">
          <motion.div animate={{
          height: scrolled ? 60 : 72
        }} transition={{
          duration: 0.3
        }} className="flex items-center justify-between" data-id="element-2729" data-mp="umj4cxpi03as">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex-shrink-0" data-id="element-2730" data-mp="14icds87x7goc">
              <motion.img animate={{
              h: scrolled ? 32 : 44
            }} src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="DR Nutrition" className={scrolled ? 'h-8' : 'h-11'} data-id="element-2731" data-mp="18mk2i5edowmk" />
            </button>

            {/* Deliver To & Search */}
            <div className={`flex-1 ${scrolled ? 'max-w-6xl md:mx-4 lg:mx-6' : 'max-w-5xl md:mx-4 lg:mx-10'} flex items-center md:gap-3 lg:gap-6`} data-id="element-2732" data-mp="1yx3ji9saxrdy">
              <button onClick={() => setLocationModalOpen(true)} className={`flex items-center gap-2 hover:opacity-80 transition-opacity whitespace-nowrap`} data-id="element-2733" data-mp="167wo3jo59hwx">
                <MapPinIcon className={`${scrolled ? 'w-4 h-4' : 'w-5 h-5'} text-[#333273]`} strokeWidth={1.5} data-id="element-2734" data-mp="1g5asgkwsmg8v" />
                <div className="text-left hidden lg:block" data-id="element-2735" data-mp="sdnc2wx5qnku">
                  {!scrolled && <div className="text-[10px] text-[#62617A] leading-none mb-0.5" data-id="element-2736" data-mp="nf2hpe4l6y4b">
                      Deliver to
                    </div>}
                  <div className={`${scrolled ? 'text-xs' : 'text-sm'} font-normal text-[#333273]`} data-id="element-2737" data-mp="33wjpwbp155sv">
                    {selectedLocation ? selectedLocation.name : 'Select Location'}
                  </div>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-[#62617A] ml-1 hidden lg:block" strokeWidth={1.5} data-id="element-2738" data-mp="3hvjddg0t3eng" />
              </button>

              <div className="flex-1 relative" data-id="element-2739" data-mp="38u20s7mlxv3i">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2740" data-mp="38ta5kxwx9zdr">
                  <SearchIcon className="w-4 h-4 text-[#62617A]" strokeWidth={1.5} data-id="element-2741" data-mp="3drtrwzffhd6n" />
                </div>
                <input type="text" placeholder="Search for supplements, brands, goals..." className={`w-full ${scrolled ? 'h-10' : 'h-11'} bg-[#F3F2FA] border border-transparent rounded-lg pl-10 pr-12 focus:outline-none focus:bg-white focus:border-[#333273] focus:ring-1 focus:ring-[#333273] transition-all text-sm text-[#333273] placeholder-[#62617A] font-normal`} value={searchQuery} onChange={handleSearchInputChange} onFocus={() => setShowSearchDropdown(true)} ref={searchInputRef} data-id="element-2742" data-mp="1xdbpwb78of3k" />
                <button className="absolute right-4 top-1/2 -translate-y-1/2 text-[#333273] hover:text-blue-700 transition-colors" data-id="element-2743" data-mp="f2zmfph87w6c">
                  <ScanBarcodeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-2744" data-mp="60hy95i0gtgp" />
                </button>
                <AnimatePresence data-id="element-2745" data-mp="ayjivuv350ua">
                  {showSearchDropdown && <motion.div ref={searchDropdownRef} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full left-0 right-0 mt-2 w-full bg-white rounded-xl shadow-2xl border border-[#DEDCF0] z-50 max-h-[80vh] overflow-y-auto p-4" data-id="element-2746" data-mp="20dzo4z5w22ew">
                      {getSearchContent()}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* User Actions */}
            <div className="flex items-center md:space-x-4 lg:space-x-6" data-id="element-2747" data-mp="3775jxdbfq221">
              {/* Language Selector */}
              <div className="relative group hidden lg:block" data-id="element-2748" data-mp="3373hv31sf6m8">
                <button className="flex items-center gap-1 text-[#62617A] hover:text-[#333273] transition-colors" data-id="element-2749" data-mp="1ctmktz1rabem">
                  <GlobeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-2750" data-mp="18tm427r6po4e" />
                  <span className="text-sm font-normal" data-id="element-2751" data-mp="37xkjt8mntxdr">{language === 'ar' ? 'AR' : 'EN'}</span>
                  <ChevronDownIcon className="w-3 h-3" strokeWidth={1.5} data-id="element-2752" data-mp="3cw5k181wwh27" />
                </button>
                <div className="absolute right-0 mt-2 w-32 bg-white border border-[#DEDCF0] rounded-lg shadow-lg z-50 hidden group-hover:block" data-id="element-2753" data-mp="3hvry1jpke1c2">
                  <div className="py-1" data-id="element-2754" data-mp="3mw67n66tdjc7">
                    <button onClick={() => setLanguage('en')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'en' ? 'bg-[#F3F2FA] text-[#333273] font-normal' : 'text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273]'}`} data-id="element-2755" data-mp="3qw6m2qtwxuj9">
                      English
                    </button>
                    <button onClick={() => setLanguage('ar')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'ar' ? 'bg-[#F3F2FA] text-[#333273] font-normal' : 'text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273]'}`} data-id="element-2756" data-mp="9ex4wvykjvb6">
                      Arabic
                    </button>
                  </div>
                </div>
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onMouseEnter={() => handleMouseEnter('account')} onMouseLeave={() => handleMouseLeave('account')} onClick={() => navigate('/account')} data-id="element-2757" data-mp="1txpsi8gvr5vv">
                <UserIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2758" data-mp="2mo7wdbpfhbac" />
                {showAccountMenu && <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-[#DEDCF0] z-50 py-2" data-id="element-2759" data-mp="2cv0s5u6fc1bu">
                    <div className="px-4 py-3 border-b border-[#DEDCF0]" data-id="element-2760" data-mp="35cw9hbho6cbk">
                      <p className="text-sm font-normal text-[#333273]" data-id="element-2761" data-mp="35e7hjni5e68h">Hello, Guest</p>
                      <button onClick={() => setAuthModalOpen(true)} className="text-xs text-[#333273] font-normal mt-1 hover:underline" data-id="element-2762" data-mp="30kl8047a6igw">
                        Sign In / Register
                      </button>
                    </div>
                    <div className="py-1" data-id="element-2763" data-mp="2b7teh6g499mu">
                      <button onClick={() => navigate('/account')} className="flex items-center px-4 py-2 text-sm text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273] w-full text-left transition-colors" data-id="element-2764" data-mp="2g4jtrrk3k177">
                        <UserIcon className="w-4 h-4 mr-3" strokeWidth={1.5} data-id="element-2765" data-mp="2u4umz18vprvj" />
                        My Account
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="flex items-center px-4 py-2 text-sm text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273] w-full text-left transition-colors" data-id="element-2766" data-mp="2z2w77pi1jx8l">
                        <PackageIcon className="w-4 h-4 mr-3" strokeWidth={1.5} data-id="element-2767" data-mp="1g4gdqkdg8bzm" />
                        My Orders
                      </button>
                    </div>
                  </div>}
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onClick={() => navigate('/account/wishlist')} data-id="element-2768" data-mp="1ox1vbx5cgcmv">
                <div className="relative" data-id="element-2769" data-mp="x7f5mqollvrg">
                  <HeartIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2770" data-mp="x8hy331kk69h" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#FFC400] text-[#333273] text-[9px] font-normal rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2771" data-mp="166txnukeyee2">
                    {wishlistCount}
                  </span>
                </div>
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onMouseEnter={() => handleMouseEnter('cart')} onMouseLeave={() => handleMouseLeave('cart')} onClick={() => navigate('/cart')} data-id="element-2772" data-mp="2cneant2x5bnh">
                <div className="relative" data-id="element-2773" data-mp="1fankde894xyx">
                  <ShoppingCartIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2774" data-mp="1jbhckjsq8yxm" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#FFC400] text-[#333273] text-[9px] font-normal rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2775" data-mp="1o7erclocdx8a">
                    {cartCount()}
                  </span>
                </div>
                {showCartMenu && cartItems.length > 0 && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50 p-4" data-id="element-2776" data-mp="15ad9w1dlq4ng">
                    <div className="max-h-60 overflow-y-auto mb-4" data-id="element-2777" data-mp="7uhk9o6dqk3l">
                      {cartItems.map(item => <div key={item.id} className="flex gap-3 mb-3" data-id="element-2778" data-mp="csj2y7zw8hej">
                          <img src={item.image} className="w-12 h-12 rounded object-cover" data-id="element-2779" data-mp="59rz61zxw9a2" />
                          <div className="flex-1 min-w-0" data-id="element-2780" data-mp="3rhi347kzv6h6">
                            <p className="text-xs font-normal truncate" data-id="element-2781" data-mp="3rk4b28bsrnzx">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2782" data-mp="3ihmmvocl0laa">
                              {item.quantity} x {item.price} SAR
                            </p>
                          </div>
                        </div>)}
                    </div>
                    <div className="border-t pt-3" data-id="element-2783" data-mp="33sgr6acydd75">
                      <div className="flex justify-between font-bold mb-3" data-id="element-2784" data-mp="33trqnxzd7ysg">
                        <span data-id="element-2785" data-mp="2zr4ytx2p2dv9">Total:</span>
                        <span data-id="element-2786" data-mp="2zq2anwwrapj1">{cartTotalValue.toFixed(2)} SAR</span>
                      </div>
                      <button onClick={() => navigate('/checkout')} className="w-full bg-[#1E2B55] text-white py-2 rounded-lg font-bold text-sm" data-id="element-2787" data-mp="2usk60ipruwgu">
                        Checkout
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Bar */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 52,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.3
    }} className="bg-white border-b border-[#E3E1EF] overflow-hidden" data-id="element-2788" data-mp="3sb3uu4htr2x">
        <div className="container mx-auto px-4" data-id="element-2789" data-mp="3uym9l3kg7g0l">
          <div className="flex h-[52px] items-center gap-5 overflow-x-auto no-scrollbar" data-id="element-2790" data-mp="3uz5ixvw18w30">
            <button onClick={() => navigate('/categories')} className="flex flex-shrink-0 items-center gap-2 rounded-md bg-[#1E2B55] px-3 py-2 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#162041]" data-id="element-2791" data-mp="3gzmog5w59j96">
              <MenuIcon className="h-4 w-4" strokeWidth={1.8} data-id="element-2792" data-mp="2xe4d0g297n4q" /> All Categories
            </button>
            <nav className="flex flex-shrink-0 items-center gap-6 whitespace-nowrap" data-id="element-2793" data-mp="3bdcbf6qler7v">
              {['Proteins', 'Vitamins', 'Pre-Workout', 'Weight Management'].map(item => <button key={item} onClick={() => navigate(`/category/${item.toLowerCase().replace(/ /g, '-')}`)} className="text-[13px] font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2794" data-mp="3lai6sr68p4dw">
                    {item}
                  </button>)}
              <button onClick={() => navigate('/category/best-sellers')} className="text-[13px] font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2795" data-mp="2xkb4wghcxpj">
                Best Sellers
              </button>
              <button onClick={() => navigate('/offers')} className="rounded-md bg-[#fff8e6] px-2.5 py-1.5 text-[13px] font-bold text-[#8a5a00] transition-colors duration-150 hover:bg-[#ffedbf]" data-id="element-2796" data-mp="bgrqe5pf3t4s">
                Deals &amp; Offers
              </button>
            </nav>
            <div className="ml-auto hidden items-center gap-4 whitespace-nowrap xl:flex" data-id="element-2797" data-mp="3nw3s7hfhfhp7">
              <button onClick={() => setLocationModalOpen(true)} className="flex items-center gap-1.5 text-xs font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2798" data-mp="6fc7aj6svwp1">
                <TruckIcon className="h-4 w-4 text-[#1E2B55]" data-id="element-2799" data-mp="1msrdfkn4ph2" />
                Delivering to {selectedLocation?.name ?? 'your location'}
              </button>
              <span className="h-4 w-px bg-gray-200" aria-hidden="true" data-id="element-2800" data-mp="354ja59cz6rq1" />
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#62617A]" data-id="element-2801" data-mp="1kn1rw13rcwyk">
                <ShieldCheckIcon className="h-4 w-4 text-[#1E2B55]" data-id="element-2802" data-mp="1pejqwjm80ngw" /> Secure checkout
              </span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#62617A]" data-id="element-2803" data-mp="1bnesfkula185">
                <StethoscopeIcon className="h-4 w-4 text-[#1E2B55]" data-id="element-2804" data-mp="1fnfglx7hpqse" /> Pharmacist support
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-2805" data-mp="28cw2ec0lu9fn" />
      <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-2806" data-mp="1udd92pcgd2ne" />
      <OrderTrackingModal isOpen={orderTrackingModalOpen} onClose={() => setOrderTrackingModalOpen(false)} data-id="element-2807" data-mp="1zad5e8ji2r1b" />
    </div>;
}